/**
 * Client/lead and related data from techforce_portal API.
 */

import { API_BASE } from '../config/apiBase';
import { AttachmentVoidFields, patchAttachmentVoid } from './attachmentVoid';

export type ClientLifecycleStatus = 'Active' | 'Void';

export interface ClientRow {
  id: number;
  company: string;
  employee_id: number | null;
  employee_name: string | null;
  point_of_contact: string;
  contact_email: string | null;
  contact_phone: string | null;
  product: string | null;
  notes: string | null;
  start_date: string | null;
  billing_address: string | null;
  site_location: string | null;
  type: 'client' | 'lead';
  source: string | null;
  industry: string | null;
  client_status: ClientLifecycleStatus;
  void_reason: string | null;
  /** Two-letter US state; drives the region letter of the job code. */
  state_code: string | null;
  /** Position in the client base. Null for leads until they convert. */
  customer_number: number | null;
  /** `<region letter>-<6 digits>`, null until both halves are known. */
  job_code: string | null;
  /** Set when the contact email has opted out of marketing email (unsubscribed, bounced or removed by staff). */
  marketing_opt_out_reason?: 'unsubscribed' | 'bounced' | 'manual' | null;
  marketing_opt_out_at?: string | null;
  /** Date of the first outreach; null when nobody has made contact yet. */
  first_contact: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrderRow {
  id: number;
  order_number: string;
  company_name: string;
  status: string;
  category: string;
  notes?: string | null;
  employee_id: number | null;
  employee_name?: string | null;
  /** Everyone assigned to the order, primary (employee_name) first. */
  assignee_names?: string[];
  tracking_number?: string | null;
  estimated_delivery_date?: string | null;
  shipping_address?: string | null;
  deliver_to?: string | null;
  installation_appointment_time?: string | null;
  site_location?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContractRow {
  id: number;
  contract_id: string;
  order_number: string;
  contract_type: string | null;
  status: string;
  generated_at: string | null;
  signed_at: string | null;
  created_at: string;
}

export interface InvoiceRow {
  id: number;
  order_number: string;
  invoice_number: string | null;
  amount: string | number | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface ClientNoteAttachmentRow extends AttachmentVoidFields {
  id: number;
  client_note_id: number;
  original_file_name: string;
  stored_file_name: string;
  uploaded_by_user_id: number | null;
  uploaded_by: string | null;
  created_at: string;
}

export interface ClientNoteRow {
  id: number;
  client_id: number;
  user_id: number | null;
  submitted_by: string | null;
  note: string;
  created_at: string;
  task_id?: number | null;
  task_name?: string | null;
  attachments?: ClientNoteAttachmentRow[];
}

export interface ClientTaskOption {
  id: number;
  name: string;
  status: string;
}

export async function fetchClients(
  type?: 'client' | 'lead',
  assignedToUserId?: number,
  clientStatus?: ClientLifecycleStatus | 'all'
): Promise<ClientRow[]> {
  const params = new URLSearchParams();
  if (type) params.set('type', type);
  if (assignedToUserId != null && assignedToUserId > 0) params.set('assigned_to_user', String(assignedToUserId));
  if (clientStatus) params.set('client_status', clientStatus);
  const url = params.toString() ? `${API_BASE}/api/clients?${params}` : `${API_BASE}/api/clients`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch clients');
  const data = await res.json();
  return data.clients || [];
}

/** The slice of a client the company pickers read back into their forms. */
export interface ClientPickerRow {
  id: number;
  company: string;
  point_of_contact: string;
  contact_email: string | null;
  contact_phone: string | null;
  billing_address: string | null;
  site_location: string | null;
}

/**
 * Active clients for the company pickers, shared by every modal that has one.
 * Those modals block on this list, so the cache keeps repeat opens instant and
 * the in-flight guard collapses the duplicate calls sibling tabs would make.
 */
const PICKER_CACHE_TTL_MS = 5 * 60 * 1000;
let pickerCache: { at: number; rows: ClientPickerRow[] } | null = null;
let pickerInFlight: Promise<ClientPickerRow[]> | null = null;

/** Cached rows if they are still fresh, so a picker can skip its loading state. */
export function cachedClientPickerOptions(): ClientPickerRow[] | null {
  if (!pickerCache || Date.now() - pickerCache.at > PICKER_CACHE_TTL_MS) return null;
  return pickerCache.rows;
}

export async function fetchClientPickerOptions(): Promise<ClientPickerRow[]> {
  const cached = cachedClientPickerOptions();
  if (cached) return cached;
  if (pickerInFlight) return pickerInFlight;
  pickerInFlight = (async () => {
    const res = await fetch(`${API_BASE}/api/clients?type=client&fields=picker`);
    if (!res.ok) throw new Error('Failed to fetch clients');
    const data = await res.json();
    const rows = (data.clients || []) as ClientPickerRow[];
    pickerCache = { at: Date.now(), rows };
    return rows;
  })();
  try {
    return await pickerInFlight;
  } finally {
    pickerInFlight = null;
  }
}

/** Drop the cache so the next picker open reflects a client that just changed. */
export function invalidateClientPickerOptions(): void {
  pickerCache = null;
}

export async function fetchClientById(id: number): Promise<ClientRow | null> {
  const res = await fetch(`${API_BASE}/api/clients/${id}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to fetch client');
  return res.json();
}

export async function fetchClientOrders(clientId: number): Promise<OrderRow[]> {
  const res = await fetch(`${API_BASE}/api/clients/${clientId}/orders`);
  if (!res.ok) throw new Error('Failed to fetch orders');
  const data = await res.json();
  return data.orders || [];
}

export async function fetchClientContracts(clientId: number): Promise<ContractRow[]> {
  const res = await fetch(`${API_BASE}/api/clients/${clientId}/contracts`);
  if (!res.ok) throw new Error('Failed to fetch contracts');
  const data = await res.json();
  return data.contracts || [];
}

export async function fetchClientInvoices(clientId: number): Promise<InvoiceRow[]> {
  const res = await fetch(`${API_BASE}/api/clients/${clientId}/invoices`);
  if (!res.ok) throw new Error('Failed to fetch invoices');
  const data = await res.json();
  return data.invoices || [];
}

export async function fetchClientNotes(clientId: number): Promise<ClientNoteRow[]> {
  const res = await fetch(`${API_BASE}/api/clients/${clientId}/notes`);
  if (!res.ok) throw new Error('Failed to fetch notes');
  const data = await res.json();
  return data.notes || [];
}

export interface ClientInstallationActivityItem {
  id: number;
  business_name: string;
  when: string | null;
  status: string;
}

export interface ClientCallLogItem {
  id: number;
  status: 'Scheduled' | 'Logged' | 'Void';
  employee_name: string | null;
  spoke_with: string | null;
  point_of_contact: string | null;
  reason: string | null;
  /** When the call happened, or is due to happen if it is still scheduled. */
  when: string | null;
}

export interface ClientInstallationActivity {
  inspections: ClientInstallationActivityItem[];
  installations: ClientInstallationActivityItem[];
  onsiteServices: ClientInstallationActivityItem[];
  signoffs: ClientInstallationActivityItem[];
  /** Scheduled and logged calls only; voided calls are excluded server-side. */
  calls: ClientCallLogItem[];
}

export async function fetchClientInstallationActivity(clientId: number): Promise<ClientInstallationActivity> {
  const res = await fetch(`${API_BASE}/api/clients/${clientId}/installation-activity`);
  if (!res.ok) throw new Error('Failed to fetch installation activity');
  return res.json();
}

export async function fetchClientTasks(clientId: number): Promise<ClientTaskOption[]> {
  const res = await fetch(`${API_BASE}/api/clients/${clientId}/tasks`);
  if (!res.ok) throw new Error('Failed to fetch client tasks');
  const data = await res.json();
  return data.tasks || [];
}

export async function createClientNote(clientId: number, note: string, userId: number, taskId?: number | null): Promise<ClientNoteRow> {
  const res = await fetch(`${API_BASE}/api/clients/${clientId}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note, user_id: userId, ...(taskId != null && taskId > 0 ? { task_id: taskId } : {}) }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to add note');
  return data;
}

export async function updateClientNote(
  clientId: number,
  noteId: number,
  note: string,
  userId: number,
  taskId?: number | null
): Promise<ClientNoteRow> {
  const res = await fetch(`${API_BASE}/api/clients/${clientId}/notes/${noteId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note, user_id: userId, ...(taskId != null && taskId > 0 ? { task_id: taskId } : {}) }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update note');
  return data;
}

export async function deleteClientNote(clientId: number, noteId: number, userId: number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/clients/${clientId}/notes/${noteId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to delete note');
}

export async function uploadClientNoteAttachment(
  clientId: number,
  noteId: number,
  file: File,
  userId: number
): Promise<ClientNoteAttachmentRow> {
  const form = new FormData();
  form.append('file', file);
  form.append('user_id', String(userId));
  const res = await fetch(`${API_BASE}/api/clients/${clientId}/notes/${noteId}/attachments`, {
    method: 'POST',
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to upload file');
  return data as ClientNoteAttachmentRow;
}

export function clientNoteAttachmentDownloadUrl(clientId: number, noteId: number, attachmentId: number): string {
  return `${API_BASE}/api/clients/${clientId}/notes/${noteId}/attachments/${attachmentId}/download`;
}

/** Sets a note's file aside with a reason. The file stays on the note and can be restored. */
export function voidClientNoteAttachment(
  clientId: number,
  noteId: number,
  attachmentId: number,
  reason: string,
  userId: number
): Promise<ClientNoteAttachmentRow> {
  return patchAttachmentVoid(
    `${API_BASE}/api/clients/${clientId}/notes/${noteId}/attachments/${attachmentId}`,
    reason,
    userId
  );
}

export function unvoidClientNoteAttachment(
  clientId: number,
  noteId: number,
  attachmentId: number,
  userId: number
): Promise<ClientNoteAttachmentRow> {
  return patchAttachmentVoid(
    `${API_BASE}/api/clients/${clientId}/notes/${noteId}/attachments/${attachmentId}`,
    null,
    userId
  );
}

// ─── Client attachment folder ──────────────────────────────────────────────

/** Where a file in the client's folder comes from; only 'client' rows can be voided here. */
export type ClientAttachmentSource = 'client' | 'note' | 'survey_photo' | 'contract' | 'signoff' | 'invoice';

export interface ClientAttachmentRow extends AttachmentVoidFields {
  key: string;
  source: ClientAttachmentSource;
  source_id: number;
  name: string;
  category: string;
  mime_type: string | null;
  byte_size: number | null;
  order_number: string | null;
  uploaded_by: string | null;
  created_at: string | null;
  /**
   * Files uploaded into the folder can be voided from it. Note files and survey
   * photos are voided where they live; the folder only reports their state.
   */
  can_void: boolean;
}

export async function fetchClientAttachments(clientId: number): Promise<ClientAttachmentRow[]> {
  const res = await fetch(`${API_BASE}/api/clients/${clientId}/attachments`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to load attachments');
  return (data as { attachments: ClientAttachmentRow[] }).attachments || [];
}

export async function uploadClientAttachment(
  clientId: number,
  file: File,
  opts: { userId?: number; title?: string } = {}
): Promise<ClientAttachmentRow> {
  const form = new FormData();
  form.append('file', file);
  if (opts.userId != null) form.append('user_id', String(opts.userId));
  if (opts.title) form.append('title', opts.title);
  const res = await fetch(`${API_BASE}/api/clients/${clientId}/attachments`, { method: 'POST', body: form });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to upload file');
  return data as ClientAttachmentRow;
}

/** Sets a folder upload aside with a reason. The file stays on the client and can be restored. */
export function voidClientAttachment(
  clientId: number,
  attachmentId: number,
  reason: string,
  userId?: number | null
): Promise<Partial<ClientAttachmentRow>> {
  return patchAttachmentVoid(`${API_BASE}/api/clients/${clientId}/attachments/${attachmentId}`, reason, userId);
}

export function unvoidClientAttachment(
  clientId: number,
  attachmentId: number,
  userId?: number | null
): Promise<Partial<ClientAttachmentRow>> {
  return patchAttachmentVoid(`${API_BASE}/api/clients/${clientId}/attachments/${attachmentId}`, null, userId);
}

export function clientAttachmentFileUrl(
  clientId: number,
  attachment: Pick<ClientAttachmentRow, 'source' | 'source_id'>,
  opts: { download?: boolean } = {}
): string {
  const base = `${API_BASE}/api/clients/${clientId}/attachments/${attachment.source}/${attachment.source_id}/file`;
  return opts.download ? `${base}?download=1` : base;
}

export interface CreateClientPayload {
  company: string;
  type: 'client' | 'lead';
  point_of_contact: string;
  contact_email?: string;
  contact_phone?: string;
  product?: string;
  notes?: string;
  start_date?: string;
  billing_address?: string;
  site_location?: string;
  /** Two-letter US state; sets the region letter of the client's job code. */
  state_code?: string;
  source?: string;
  industry?: string;
  first_contact?: string;
  employee_name?: string;
  user_id?: number;
}

/** The client or lead already using a company name. */
export interface ExistingClientRef {
  id: number;
  company: string;
  type: 'client' | 'lead';
}

/**
 * A create was refused because the company name is taken. Carries the record
 * that owns the name so the UI can link straight to it.
 */
export class DuplicateClientError extends Error {
  readonly existing: ExistingClientRef;

  constructor(message: string, existing: ExistingClientRef) {
    super(message);
    this.name = 'DuplicateClientError';
    this.existing = existing;
  }
}

/** Turns a 409 that names the offending record into a DuplicateClientError. */
export function duplicateClientError(status: number, data: unknown): DuplicateClientError | null {
  if (status !== 409) return null;
  const existing = (data as { existing?: ExistingClientRef } | null)?.existing;
  if (!existing || typeof existing.id !== 'number') return null;
  const message = (data as { error?: string }).error || `"${existing.company}" already exists.`;
  return new DuplicateClientError(message, {
    id: existing.id,
    company: existing.company,
    type: existing.type === 'lead' ? 'lead' : 'client',
  });
}

/** Detail-page route for a client or lead. */
export function clientRecordPath(ref: Pick<ExistingClientRef, 'id' | 'type'>): string {
  return ref.type === 'lead' ? `/lead/${ref.id}` : `/client/${ref.id}`;
}

export async function createClient(payload: CreateClientPayload): Promise<ClientRow> {
  const res = await fetch(`${API_BASE}/api/clients`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw duplicateClientError(res.status, data) ?? new Error(data.error || 'Failed to create client');
  invalidateClientPickerOptions();
  return data;
}

export interface UpdateClientPayload {
  company?: string;
  user_id?: number | null;
  employee_name?: string | null;
  start_date?: string | null;
  first_contact?: string | null;
  point_of_contact?: string;
  contact_email?: string | null;
  contact_phone?: string | null;
  billing_address?: string | null;
  site_location?: string | null;
  /** Two-letter US state; changing it changes the client's region letter. */
  state_code?: string | null;
  notes?: string | null;
  industry?: string | null;
  source?: string | null;
  product?: string | null;
  client_status?: ClientLifecycleStatus;
  void_reason?: string | null;
}

export async function voidClient(id: number, reason: string): Promise<ClientRow> {
  return updateClient(id, { client_status: 'Void', void_reason: reason });
}

export async function unvoidClient(id: number): Promise<ClientRow> {
  return updateClient(id, { client_status: 'Active' });
}

export async function updateClient(id: number, payload: UpdateClientPayload): Promise<ClientRow> {
  const res = await fetch(`${API_BASE}/api/clients/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update client');
  invalidateClientPickerOptions();
  return data;
}

/** Promote a lead to a client. Server sets type='client' and fills in start_date if unset. */
export async function convertLeadToClient(id: number): Promise<ClientRow> {
  const res = await fetch(`${API_BASE}/api/clients/${id}/convert-to-client`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to convert lead to client');
  invalidateClientPickerOptions();
  return data as ClientRow;
}

/**
 * Demote a client back to a lead — the undo for a mistaken promotion.
 *
 * The record keeps its id, so everything attached to it stays attached, and it
 * keeps its customer number so a re-promotion restores the same job code.
 * `source` is only sent when the record has none to fall back on.
 */
export async function convertClientToLead(id: number, source?: string): Promise<ClientRow> {
  const res = await fetch(`${API_BASE}/api/clients/${id}/convert-to-lead`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(source?.trim() ? { source: source.trim() } : {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to convert client to lead');
  invalidateClientPickerOptions();
  return data as ClientRow;
}

export async function deleteClient(id: number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/clients/${id}`, { method: 'DELETE' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to delete client');
  invalidateClientPickerOptions();
}
